<script setup lang="ts">
import getAuditLogsQueryParameter from '@/types/requests/getAuditLogs';
import AuditLogList from '@/components/AuditLog/AuditLogList.vue';
import getAuditLogsResponse from '@/types/responses/getAuditLogs';
import { AUDITABLE_TYPES } from "@/services/consts/audit_logs";
import SearchForm from '@/components/AuditLog/SearchForm.vue';
import { useAuth } from "@/services/user/Auth0UserProvider";
import PaginateWithCount from "@/components/PaginateWithCount.vue";
import { formatDate } from "@/services/functions";
import Api from "@/services/api/ApiServiceFabrick";
import auditLog from '@/types/responses/auditLog';
import { Ref, ref, reactive, watch } from "vue";
import i18n from "@/lang/i18n";

document.title = i18n.global.t('pages.auditLog.title');
const { loginWithRedirect } = useAuth()
const totalPage: Ref<number> = ref(0)
const itemsPerPage: Ref<number> = ref(50)
const allElementsCount: Ref<number> = ref(0)
const currentPage: Ref<number> = ref(1)
const isLoading: Ref<boolean> = ref(false)
const auditLogs: Ref<auditLog[]> = ref([])
const isSearchCompleted: Ref<boolean> = ref(false)
const searchParams: getAuditLogsQueryParameter = reactive({
  page: 1,
  auditable_type: null,
  auditable_id: null,
  event: '',
  operation_office_id: null,
  user_id: null,
  daterange: ''
})
const errors = reactive({
  operation_office_id: [] as string[],
  daterange: [] as string[]
})

function getAuditLogs() {
  isLoading.value = true
  Api().getAuditLogs(searchParams).then((data: getAuditLogsResponse) => {
    auditLogs.value = data.data
    totalPage.value = data.total_page
    allElementsCount.value = data.total
    itemsPerPage.value = data.per_page
    currentPage.value = data.current_page
    isSearchCompleted.value = true
  }).catch((error) => {
    errorHandler(error)
  }).finally(() => {
    isLoading.value = false
  })
}

function errorHandler(error: any) {
  const errorResponse = error.response
  if (errorResponse.status === 422) {
    errors.operation_office_id = errorResponse.data.errors?.operation_office_id
  }
  if (errorResponse.status === 403) {
    loginWithRedirect()
  }
}

function searchList(values: any) {
  clearValidation()
  searchParams.page = values.page
  searchParams.auditable_type = values.auditable_type ? [values.auditable_type] : Object.keys(AUDITABLE_TYPES).flat()
  searchParams.auditable_id = values.auditable_id ? values.auditable_id : null
  searchParams.event = values.event ? values.event : ''
  searchParams.operation_office_id = values.operationOffice ? values.operationOffice.id : null
  searchParams.user_id = values.account ? values.account.id : null
  searchParams.daterange = values.daterange ? `${formatDate(values.daterange[0], '/')} - ${formatDate(values.daterange[1], '/')}` : ''
  getAuditLogs()
}

function clearValidation() {
  errors.operation_office_id = []
  errors.daterange = []
}

watch(currentPage, () => {
  searchParams.page = currentPage.value
  getAuditLogs()
})
</script>
<template>
  <div class="d-flex" style="min-height: 45vw;">
    <div class="content p-4 w-100">
      <div class="d-flex justify-content-between mb-1">
        <h4 class="card-title mt-0">
          {{ i18n.global.t('pages.auditLog.title') }}
        </h4>
      </div>
      <div>
        <AuditLogList
          class="mb-2"
          :isLoading="isLoading"
          :isSearchCompleted="isSearchCompleted"
          :auditLogs="auditLogs"
        />
        <PaginateWithCount
          class="justify-end"
          :totalPage="totalPage"
          v-model="currentPage"
          :itemsPerPage="itemsPerPage"
          :allElementsCount="allElementsCount"
          :showItemCount="true"
        />
      </div>
    </div>
    <SearchForm
      :isLoading="isLoading"
      :errors="errors"
      @search="searchList"
    />
  </div>
</template>
